import { useState } from "react";
import {
  Sparkles, MessageCircleQuestion, Bookmark, BookmarkCheck, ThumbsUp, ThumbsDown,
  Volume2, Square, Loader2, MessageSquare, ExternalLink, BellOff, Pin, ListPlus, Columns3, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { wholeStory, sameHeadline } from "@/lib/storyRead";
import { CommentThread } from "@/components/news/CommentThread";
import { DiscussButton } from "@/components/news/DiscussButton";
import { StoryTile } from "@/components/news/StoryTile";
import { ShareStory } from "@/components/news/ShareStory";

const ago = (published) => {
  if (!published) return "";
  const then = new Date(published);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 60) return `${Math.max(1, mins)} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
};

export const StoryCard = ({ story, speaker, onMuted, onSavedChange, label, skin, onSkin,
                            pinned = false, onPin, lang = "", onQueue, queued = false,
                            alsoTitle = "", onCompare }) => {
  const [row, setRow] = useState(story);
  const [broken, setBroken] = useState(false);
  const [asking, setAsking] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [showTalk, setShowTalk] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const votes = row.votes || { up: 0, down: 0, mine: 0 };

  const react = async (vote) => {
    try {
      const { data } = await api.post("/news/react", {
        story_id: row.story_id, vote: votes.mine === vote ? 0 : vote,
        title: row.title, url: row.url, source: row.source || "",
      });
      setRow((r) => ({ ...r, votes: data.votes }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    }
  };

  const toggleSave = async () => {
    try {
      if (row.saved) {
        await api.delete(`/news/saved/${row.story_id}`);
      } else {
        await api.post("/news/saved", {
          story_id: row.story_id, title: row.title, url: row.url,
          source: row.source || "", snippet: row.snippet || "",
        });
      }
      setRow((r) => ({ ...r, saved: !r.saved }));
      onSavedChange?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that story");
    }
  };

  const mute = async () => {
    try {
      const { data } = await api.post("/news/mute", { source: row.source });
      toast.success(data.muted ? `${row.source} muted` : `${row.source} is back`);
      onMuted?.(data.muted_sources);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not mute that publisher");
    }
  };

  const ask = async () => {
    const q = question.trim();
    if (!q || asking) return;
    setAsking(true);
    try {
      const { data } = await api.post("/home/news/ask",
        { title: row.title, snippet: row.snippet || "", question: q });
      setAnswer({ q, text: data.answer });
      setQuestion("");
      speaker.play(row.story_id, async () => data.answer);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "MiLu could not answer that");
    } finally {
      setAsking(false);
    }
  };

  // a headline opened on the publisher's site still counts as reading
  const noteOpen = () =>
    api.post("/news/story/opened", {
      url: row.url, story_id: row.story_id || "", title: row.title || "",
      source: row.source || "", topic: label || "",
    }).catch(() => {});

  const listen = () =>
    speaker.play(`${row.story_id}-read`, wholeStory({ ...row, topic: label || "" }, lang));

  const iconButton = "inline-flex min-h-[16px] items-center gap-1 rounded-full border border-white/10 px-[4.9px] text-[6.1px] text-ink-soft transition-colors hover:border-brand/50 hover:text-ink";
  // everything but Mute sits 20% larger
  const actionButton = "inline-flex min-h-[19px] items-center gap-1 rounded-full border border-white/10 px-[6.5px] text-[7.7px] text-ink-soft transition-colors hover:border-brand/50 hover:text-ink";

  return (
    <article className="overflow-hidden rounded-2xl border border-white/10 bg-panel transition-colors hover:border-brand/30"
             data-testid={`story-${row.story_id}`}>
      <a href={row.url} target="_blank" rel="noreferrer" onClick={noteOpen}
         data-testid={`image-${row.story_id}`}>
        {row.image && !broken ? (
          <img
            src={row.image}
            alt=""
            loading="lazy"
            onError={() => setBroken(true)}
            className="h-32 w-full object-cover"
          />
        ) : (
          <StoryTile source={row.source} label={label} skin={skin}
                     onSkin={onSkin ? (i) => onSkin(row.source, i) : undefined} />
        )}
      </a>
      <div className="p-3">
      <a href={row.url} target="_blank" rel="noreferrer" onClick={noteOpen}
         data-testid={`open-${row.story_id}`}
         className="group flex items-start gap-2 text-sm font-semibold leading-snug hover:text-brand"
         dir="auto">
        <span className="min-w-0">{row.title}</span>
        <ExternalLink className="mt-1 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-70" />
      </a>
      {alsoTitle && !sameHeadline(alsoTitle, row.title) && (
        <p className="mt-1 line-clamp-2 text-xs italic text-ink-soft" dir="auto"
           data-testid={`story-also-${row.story_id}`}>
          {alsoTitle}
        </p>
      )}
      <p className="mt-1 text-[11px] text-ink-soft">
        {row.source}{ago(row.published) ? ` · ${ago(row.published)}` : ""}
        {row.read_min ? (
          <span data-testid={`read-min-${row.story_id}`} className="text-brand">
            {` · ${row.read_est ? "~" : ""}${row.read_min} min read`}
          </span>
        ) : null}
      </p>

      {row.image && row.snippet && !sameHeadline(row.snippet, row.title) && (
        <p className="mt-2 line-clamp-2 text-xs text-ink-soft" data-testid={`summary-${row.story_id}`} dir="auto">
          {row.snippet}
        </p>
      )}

      {row.explain && (
        <p className="mt-2 flex gap-1.5 text-xs leading-snug text-ink-soft" data-testid={`explain-${row.story_id}`} dir="auto">
          <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-brand" /> {row.explain}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <button data-testid={`ask-${row.story_id}`} onClick={() => setAskOpen((v) => !v)} className={actionButton}>
          <MessageCircleQuestion className="h-[8.1px] w-[8.1px] text-brand" /> Ask MiLu
        </button>
        <button data-testid={`listen-${row.story_id}`} onClick={listen} className={actionButton}>
          {speaker.loading === `${row.story_id}-read` ? <Loader2 className="h-[8.1px] w-[8.1px] animate-spin" />
            : speaker.playing === `${row.story_id}-read` ? <Square className="h-[8.1px] w-[8.1px] text-brand" />
            : <Volume2 className="h-[8.1px] w-[8.1px] text-brand" />}
          {speaker.loading === `${row.story_id}-read` ? "Getting it…"
            : speaker.playing === `${row.story_id}-read` ? "Stop" : "Listen"}
        </button>
        {onCompare && (
          <button
            data-testid={`compare-${row.story_id}`}
            onClick={() => onCompare(row)}
            title="See how each country you follow covers this"
            className={actionButton}
          >
            <Columns3 className="h-[8.1px] w-[8.1px] text-brand" /> Compare
          </button>
        )}
        {onQueue && (
          <button
            data-testid={`queue-add-${row.story_id}`}
            onClick={() => onQueue(row)}
            title="Line it up to listen later"
            className={`${actionButton} ${queued ? "border-brand/50 text-brand" : ""}`}
          >
            <ListPlus className="h-[8.1px] w-[8.1px] text-brand" /> {queued ? "Queued" : "Queue"}
          </button>
        )}
        <button data-testid={`share-${row.story_id}`} onClick={() => setShowShare((v) => !v)}
                title="Send it to a friend in their own language"
                className={`${actionButton} ${showShare ? "border-brand/50 text-brand" : ""}`}>
          <Share2 className="h-[8.1px] w-[8.1px] text-brand" /> Share
        </button>
        <button data-testid={`save-${row.story_id}`} onClick={toggleSave}
                className={`${actionButton} ${row.saved ? "border-brand/50 text-brand" : ""}`}>
          {row.saved ? <BookmarkCheck className="h-[8.1px] w-[8.1px]" /> : <Bookmark className="h-[8.1px] w-[8.1px]" />}
          {row.saved ? "Saved" : "Save"}
        </button>
        <button data-testid={`up-${row.story_id}`} onClick={() => react(1)}
                className={`${actionButton} ${votes.mine === 1 ? "border-brand/50 text-brand" : ""}`}>
          <ThumbsUp className="h-[8.1px] w-[8.1px]" /> {votes.up}
        </button>
        <button data-testid={`down-${row.story_id}`} onClick={() => react(-1)}
                className={`${actionButton} ${votes.mine === -1 ? "border-brand/50 text-brand" : ""}`}>
          <ThumbsDown className="h-[8.1px] w-[8.1px]" /> {votes.down}
        </button>
        <button data-testid={`talk-${row.story_id}`} onClick={() => setShowTalk((v) => !v)} className={actionButton}>
          <MessageSquare className="h-[8.1px] w-[8.1px]" /> {row.comments || 0}
        </button>
        <DiscussButton story={row} className={actionButton} big />
        {onPin && row.source && (
          <button
            data-testid={`pin-${row.story_id}`}
            onClick={() => onPin(row.source, !pinned)}
            title={pinned ? `${row.source} leads your feed — tap to unpin`
                          : `Let ${row.source} lead your feed`}
            className={`${iconButton} ms-auto ${pinned ? "border-brand/60 text-brand" : ""}`}
          >
            <Pin className="h-[6.5px] w-[6.5px]" /> {pinned ? "Pinned" : "Pin"}
          </button>
        )}
        <button
          data-testid={`mute-${row.story_id}`}
          onClick={mute}
          title={`Stop showing stories from ${row.source}`}
          className={`${iconButton} ${onPin && row.source ? "" : "ms-auto"}`}
        >
          <BellOff className="h-[6.5px] w-[6.5px]" /> Mute
        </button>
      </div>

      {askOpen && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            data-testid={`ask-input-${row.story_id}`}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="What do you want to know?"
            className="min-h-[36px] min-w-0 flex-1 rounded-full border border-white/15 bg-page px-3 text-xs outline-none focus:border-brand/50"
          />
          <button
            data-testid={`ask-send-${row.story_id}`}
            onClick={ask}
            disabled={asking || !question.trim()}
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
          >
            {asking ? <Loader2 className="h-[6.7px] w-[6.7px] animate-spin" /> : <Volume2 className="h-[6.7px] w-[6.7px]" />}
            Ask
          </button>
        </div>
      )}

      {answer && (
        <div className="mt-3 rounded-2xl border border-brand/30 bg-brand/5 p-3" data-testid={`answer-${row.story_id}`}>
          <p className="text-xs font-semibold text-brand" dir="auto">{answer.q}</p>
          <p className="mt-1 text-sm" dir="auto">{answer.text}</p>
          <button
            onClick={() => speaker.play(row.story_id, async () => answer.text)}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-brand hover:underline"
          >
            {speaker.playing === row.story_id ? <Square className="h-[6.7px] w-[6.7px]" /> : <Volume2 className="h-[6.7px] w-[6.7px]" />}
            {speaker.playing === row.story_id ? "Stop" : "Hear it again"}
          </button>
        </div>
      )}

      {showShare && <ShareStory story={row} onClose={() => setShowShare(false)} />}

      {showTalk && (
        <CommentThread story={row} onCount={(n) => setRow((r) => ({ ...r, comments: n }))} />
      )}
      </div>
    </article>
  );
};
